// Conexão app-wide do socket /inbox atrelada ao auth. O socket é criado com
// autoConnect:false (client.ts) e nada mais dispara ws/connect — sem isto o
// gateway nunca marca presença e o heartbeat (presence.ts) vira no-op.
// Observa auth.isAuthed e age só na transição (idempotente).
import type { Action } from '@reduxjs/toolkit';
import type { WsConnectAction, WsDisconnectAction } from './types';

interface AuthShape {
  auth?: { isAuthed: boolean };
}

// Subset mínimo do Store que consumimos — dispatch aceita as WsAction (que o
// wsMiddleware intercepta). Evita exigir o tipo completo do RootState aqui.
interface AuthStore {
  getState: () => AuthShape;
  subscribe: (listener: () => void) => () => void;
  dispatch: (action: Action) => unknown;
}

const CONNECT: WsConnectAction = { type: 'ws/connect', namespace: 'inbox' };
const DISCONNECT: WsDisconnectAction = { type: 'ws/disconnect', namespace: 'inbox' };

let unsubscribe: (() => void) | null = null;

/**
 * Conecta o /inbox quando o user autentica e desconecta no logout. Idempotente:
 * segunda chamada não registra outra subscrição. Roda no boot via AppProviders.
 */
export function attachWsConnection(store: AuthStore): void {
  if (unsubscribe) return;

  let lastAuthed = store.getState().auth?.isAuthed ?? false;
  if (lastAuthed) store.dispatch(CONNECT);

  unsubscribe = store.subscribe(() => {
    const authed = store.getState().auth?.isAuthed ?? false;
    if (authed === lastAuthed) return;
    lastAuthed = authed;
    store.dispatch(authed ? CONNECT : DISCONNECT);
  });
}

/** Helper de testes: remove a subscrição. */
export function detachWsConnection(): void {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
